// The words a patient is sent about times.
//
// From the scheduling demo: the team liked one message, held times and a
// link that expires, and asked for the copy to be "clearer and more
// directional". So every message here does three things in order — says what
// the times are for, says exactly what to do, and gives the real deadline.
//
// It was written in three places with three wordings (slotsDraftMessage,
// heldOfferMessage, bookingInvite); they delegate here now, so the patient
// reads one voice and the exact text is a tested literal.
//
// Dates read "Mon, Oct 5 at 10:00 AM": a month in letters and a twelve-hour
// clock, always. The practice's own display preference is for the practice;
// the person reading this has no account and may be eighty.

export interface OfferSlot {
  date?: string | null;
  time?: string | null;
  specialist?: string | null;
  serviceType?: string | null;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const clock = (hours: number, minutes: number): string => {
  const ampm = hours >= 12 ? 'PM' : 'AM';
  return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

/** "Mon, Oct 5 at 10:00 AM". The date is read from its digits, never parsed as UTC. */
export function offerWhen(slot: OfferSlot): string {
  const d = String(slot.date || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const t = String(slot.time || '').match(/^(\d{1,2}):(\d{2})/);
  const day = d
    ? `${WEEKDAYS[new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3])).getDay()]}, ${MONTHS[Number(d[2]) - 1]} ${Number(d[3])}`
    : (slot.date || 'Date TBD');
  const time = t ? clock(Number(t[1]), Number(t[2])) : 'Time TBD';
  return `${day} at ${time}`;
}

/** When a link stops working, in the clinic's own time: "Tue, Oct 6 at 4:00 PM". */
export function heldUntilLabel(expiresAt: string | Date | null | undefined, timeZone: string): string {
  if (!expiresAt) return '';
  const date = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${get('weekday')}, ${get('month')} ${get('day')} at ${get('hour')}:${get('minute')} ${get('dayPeriod')}`;
}

const firstName = (full: string | null | undefined): string =>
  String(full || '').trim().split(/\s+/)[0] || 'there';

const clean = (v: string | null | undefined): string => String(v || '').trim();

/** "Consultation with Dr. Victor Levitan", with whatever of the two is known. */
const describe = (service: string, provider: string): string =>
  `${service || 'appointment'}${provider ? ` with ${provider}` : ''}`;

interface Appointment { service: string; provider: string; slots: OfferSlot[]; }

// One appointment per (service, provider) pair, in the order they first
// appear. Two kinds in one offer are two visits the patient needs — a lab
// draw and then the doctor — not a choice between them.
function appointmentsOf(slots: OfferSlot[]): Appointment[] {
  const out: Appointment[] = [];
  slots.forEach((slot) => {
    const service = clean(slot.serviceType);
    const provider = clean(slot.specialist);
    const found = out.find((a) => a.service === service && a.provider === provider);
    if (found) found.slots.push(slot);
    else out.push({ service, provider, slots: [slot] });
  });
  return out;
}

export const OFFER_TAGS = ['firstName', 'service', 'provider', 'times', 'link', 'heldUntil'] as const;
const KNOWN: readonly string[] = OFFER_TAGS;

/**
 * The standard wording, written with its tags — what Admin offers as the
 * place to start from. Filled in, it reads exactly as the standard message
 * does for a choice of times.
 */
export const STANDARD_OFFER_TEMPLATE =
  'Hi {firstName} — we are holding these times for your {service} with {provider}:\n\n{times}\n\n'
  + 'To book, tap this link and choose one:\n{link}\n\nWe will hold them until {heldUntil}.';

export interface HeldOfferInput {
  patientName: string;
  slots: OfferSlot[];
  url: string;
  expiresAt?: string | Date | null;
  timeZone: string;
  /** The practice's own wording, from Admin. Blank means the standard one. */
  template?: string | null;
}

/** The text that goes with a link holding times. */
export function heldOfferText({ patientName, slots, url, expiresAt, timeZone, template }: HeldOfferInput): string {
  const appointments = appointmentsOf(slots);
  const until = heldUntilLabel(expiresAt, timeZone);
  const hi = `Hi ${firstName(patientName)}`;

  if (appointments.length > 1) {
    const anyChoice = appointments.some((a) => a.slots.length > 1);
    const both = appointments.length === 2 ? 'both' : `all ${appointments.length}`;
    const blocks = appointments.map((a, i) =>
      [`${i + 1}. ${describe(a.service, a.provider)}`, ...a.slots.map(offerWhen)].join('\n'));
    return [
      `${hi} — we are holding ${appointments.length} appointments for you:`,
      ...blocks,
      `To book ${both}, tap this link${anyChoice ? ' and choose a time for each' : ''}:\n${url}`,
      ...(until ? [`We will hold them until ${until}.`] : []),
    ].join('\n\n');
  }

  const only = appointments[0] || { service: '', provider: '', slots: [] };
  const times = only.slots.map(offerWhen).join('\n\n');

  // The practice's own wording, for the one case a template can describe.
  // A misspelt placeholder is left as typed, so the mistake is visible in
  // the reply box instead of vanishing into an empty space.
  if (clean(template)) {
    const values: Record<string, string> = {
      firstName: firstName(patientName), service: only.service || 'appointment', provider: only.provider,
      times, link: url, heldUntil: until,
    };
    return String(template).trim().replace(/\{(\w+)\}/g, (whole, name: string) =>
      (KNOWN.includes(name) ? values[name] : whole));
  }

  const one = only.slots.length === 1;
  return [
    `${hi} — we are holding ${one ? 'this time' : 'these times'} for your ${describe(only.service, only.provider)}:`,
    times,
    `To book, tap this link and ${one ? 'confirm it' : 'choose one'}:\n${url}`,
    ...(until ? [`We will hold ${one ? 'it' : 'them'} until ${until}.`] : []),
  ].join('\n\n');
}

/**
 * Times offered in a text with no link. Nothing is held, so it never says
 * "holding"; the patient answers by replying, so it says how.
 */
export function plainOfferText(slots: OfferSlot[], opts: { lead?: string | null } = {}): string {
  const lead = clean(opts.lead);
  const body = plainBody(slots);
  return lead ? `${lead}\n\n${body}` : body;
}

function plainBody(slots: OfferSlot[]): string {
  if (!slots.length) return 'Here are times we can offer:';
  const appointments = appointmentsOf(slots);

  if (appointments.length > 1) {
    // One item per appointment, with its options — not one item per time,
    // which read "1. lab Oct 7, 2. doctor Oct 13, 3. lab Oct 8 — Reply YES
    // to book these" and told the patient to book all three.
    const anyChoice = appointments.some((a) => a.slots.length > 1);
    const lines = appointments.map((a, i) =>
      `${i + 1}. ${describe(a.service, a.provider)} — ${a.slots.map(offerWhen).join(' or ')}`);
    return ['Here are the appointments we can offer:', ...lines,
      anyChoice
        ? 'Reply with the time you would like for each and we will book them.'
        : 'Reply YES to book these, or tell us what would suit you better.'].join('\n\n');
  }

  const what = describe(appointments[0].service, appointments[0].provider);
  if (slots.length === 1) {
    return [`Here is a time for your ${what}:`, offerWhen(slots[0]),
      'Reply YES to book it, or tell us what would suit you better.'].join('\n\n');
  }
  return [`Here are times for your ${what}:`, ...slots.map((s, i) => `${i + 1}. ${offerWhen(s)}`),
    'Reply with the number you would like and we will book it.'].join('\n\n');
}

export interface OpenLinkInput {
  patientName: string;
  service?: string | null;
  provider?: string | null;
  url: string;
  expiresAt?: string | Date | null;
  timeZone: string;
  /** false when it is added to a message that already greets the patient. */
  greet?: boolean;
}

/** The text that goes with a link to the open calendar — no times held. */
export function openLinkText({ patientName, service, provider, url, expiresAt, timeZone, greet = true }: OpenLinkInput): string {
  const until = heldUntilLabel(expiresAt, timeZone);
  const ask = `to choose a time for your ${describe(clean(service), clean(provider))}, tap this link:\n${url}`;
  const first = greet ? `Hi ${firstName(patientName)} — ${ask}` : `${ask.charAt(0).toUpperCase()}${ask.slice(1)}`;
  return until ? `${first}\n\nThe link works once, until ${until}.` : first;
}
